import { PreviewChangesResponse } from './types';

/**
 * Shared "were there changes worth surfacing" computation for the post-analysis
 * toast (`useMatterAiAnalysisPolling`) and the pending-review banner
 * (`PendingAiReviewBanner`). Both consume the same `preview-changes` response;
 * this is the single source of truth so the two surfaces can't independently
 * drift on what counts as "something happened" (LF-4687 — they previously
 * each hand-rolled a create-only filter that ignored updates to existing
 * contacts/assets/matter fields).
 */
export interface PreviewChangesCounts {
  newContacts: number;
  newInventoryItems: number;
  updatedContacts: number;
  updatedInventoryItems: number;
  matterUpdated: boolean;
  /**
   * Observations about records already on the matter (VALE-2007). Each is a
   * note addition on a record the review draws, so an extraction holding only
   * these still has something for the reviewer to accept.
   */
  existingItemNotes: number;
  hasNewRecords: boolean;
  hasUpdates: boolean;
  hasChanges: boolean;
}

export function getPreviewChangesCounts(
  data: PreviewChangesResponse
): PreviewChangesCounts {
  const newContacts = data.summary.contacts.new;
  const newInventoryItems = data.summary.inventoryItems.new;
  const updatedContacts = data.summary.contacts.updated;
  const updatedInventoryItems = data.summary.inventoryItems.updated;
  const matterUpdated = data.summary.matter.updated;
  const existingItemNotes = data.existingItemNotes?.length ?? 0;
  const hasNewRecords = newContacts + newInventoryItems > 0;
  const hasUpdates =
    updatedContacts + updatedInventoryItems > 0 ||
    matterUpdated ||
    existingItemNotes > 0;

  return {
    newContacts,
    newInventoryItems,
    updatedContacts,
    updatedInventoryItems,
    matterUpdated,
    existingItemNotes,
    hasNewRecords,
    hasUpdates,
    hasChanges: hasNewRecords || hasUpdates
  };
}

/**
 * Plain-English inventory of what an extraction is proposing (VALE-757).
 *
 * The retired banner said only "Existing matter details were updated", which
 * left the lawyer unable to tell what had changed or why they were being shown
 * it — the specific complaint that produced this ticket. Nothing here is
 * approximate: every clause is a real count off the preview summary, so the
 * sentence can always be acted on.
 */
export function describePreviewChanges(counts: PreviewChangesCounts): string {
  const plural = (n: number, word: string) =>
    `${n} ${word}${n === 1 ? '' : 's'}`;
  const parts: string[] = [];

  if (counts.newContacts > 0)
    parts.push(`${plural(counts.newContacts, 'contact')} to add`);
  if (counts.newInventoryItems > 0)
    parts.push(`${plural(counts.newInventoryItems, 'asset')} to add`);
  if (counts.updatedContacts > 0)
    parts.push(`${plural(counts.updatedContacts, 'contact')} to update`);
  if (counts.updatedInventoryItems > 0)
    parts.push(`${plural(counts.updatedInventoryItems, 'asset')} to update`);
  if (counts.matterUpdated) parts.push('estate details to update');
  if (counts.existingItemNotes > 0)
    parts.push(
      `${plural(counts.existingItemNotes, 'note')} to add to existing records`
    );

  if (parts.length === 0) return 'No changes proposed.';
  if (parts.length === 1) return `${parts[0]}.`;

  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}.`;
}
